import CheckBox from "components/foundation/CheckBox/CheckBox";
import DocumentTextField from "components/foundation/DocumentTextField/DocumentTextField";
import React, { useEffect, useState } from "react";
import { Controller, get, useFormContext } from "react-hook-form";
import styled from "styled-components";
import CreateDocumentForm from "../../CreateDocumentForm";
import CreateDocumentClassFormBody from "../CreateDocumentClassFormBody";
import { TempDocumentDetailResponseWithAny } from "features/document/hooks/queries/useTempDocumentDetailQuery";
import { LEAVE_APPLICATION_FIELD as FIELD } from "config/documentConstant";
import useUserDayOffQuery from "features/user/hooks/queries/useUserDayOffQuery";
import useUserDetailQuery from "features/user/hooks/queries/useUserDetailQuery";
import DateRangePicker from "components/foundation/DateRangePicker";
import formatMinMaxLengthMessage from "utils/formatUtils/formatMinMaxLengthMessage";
import Modal from "components/foundation/Modal/Modal";
import { countBusinessDaysInRange } from "utils/dateUtility";
import { useHolidaysQuery } from "features/document/utils/getKoreanHolidaysRequest";
import dayjs from "dayjs";

/* 휴가신청서 */
interface ExpenseReportForm {
  [FIELD.NAME.key]: string;
  [FIELD.COMPANY_POSITION.key]: string;
  [FIELD.LEAVE_TYPE.key]: string;
  [FIELD.LEAVE_REASON.key]: string;
  [FIELD.START_DATE.key]: string;
  [FIELD.END_DATE.key]: string;
  [FIELD.TOTAL_DAYS.key]: number;
  [FIELD.REMAINING_PAID_LEAVE.key]: number;
  [FIELD.REMAINING_ANNUAL_LEAVE.key]: number;
  [FIELD.NOTES.key]: string;
}

interface LeaveApplicationProps {
  tempDocumentDetail?: TempDocumentDetailResponseWithAny;
}

const LeaveApplicationForm = ({
  tempDocumentDetail,
}: LeaveApplicationProps) => {
  /* 폼 */
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<ExpenseReportForm>();

  // 기간 유효성 검사 조건 등록
  useEffect(() => {
    const requiredMessage = "기간 입력 필수";
    register(FIELD.START_DATE.key, {
      required: requiredMessage,
    });
    register(FIELD.END_DATE.key, {
      required: requiredMessage,
    });
  }, []);

  /* 휴가 신청자 정보 */
  const { data: currentUser } = useUserDetailQuery();

  useEffect(() => {
    if (currentUser) {
      setValue(FIELD.NAME.key, currentUser.name);
      setValue(FIELD.COMPANY_POSITION.key, currentUser.position);
    }
  }, [currentUser]);

  const { data: userDayOffResponse } = useUserDayOffQuery({
    dayOffYear: new Date().getFullYear(),
  });

  useEffect(() => {
    const dayOffCount = userDayOffResponse?.dayOffInfo.dayOffCount || 0;
    const maxDayOffCount = userDayOffResponse?.dayOffInfo.maxDayOffCount || 0;

    setValue(FIELD.REMAINING_PAID_LEAVE.key, dayOffCount);
    setValue(FIELD.REMAINING_ANNUAL_LEAVE.key, maxDayOffCount);
  }, [userDayOffResponse]);

  /* 기간 설정 에러 모달 */
  const [
    openInsufficientRemainingLeaveModal,
    setOpenInsufficientRemainingLeaveModal,
  ] = useState(false);

  /* 반차 기간 설정 에러 모달 */
  const [openHalfDayLeaveErrorModal, setOpenHalfDayLeaveErrorModal] =
    useState(false);

  /* 기간 초기화 */
  const [resetDateRange, setResetDateRange] = useState(false);

  /**
   * @description 휴가일 수 계산 함수
   */

  const startDate = watch(FIELD.START_DATE.key);

  // 선택한 시작 날짜를 기준으로 해당 년도의 공휴일 조회
  const { data: holidays } = useHolidaysQuery(dayjs(startDate).year());

  const holidayListFormatted = holidays?.map((holiday) =>
    dayjs(holiday).format("YYYY-MM-DD"),
  );

  const calculateTotalDays = () => {
    // Safari에서 yyyy.mm.dd 사용 불가 -> .를 -로 치환
    const startDate = watch(FIELD.START_DATE.key)?.replace(/\./gi, "-");
    const endDate = watch(FIELD.END_DATE.key)?.replace(/\./gi, "-");
    const leaveType = watch(FIELD.LEAVE_TYPE.key);

    if (startDate && endDate) {
      const dayOffCount = userDayOffResponse?.dayOffInfo.dayOffCount || 0;
      // 평일만 계산 (주말 제외)
      const businessDays = countBusinessDaysInRange(
        startDate,
        endDate,
        holidayListFormatted,
      );
      const totalDays = leaveType === "종일" ? businessDays : businessDays / 2;
      const newRemainingLeave = dayOffCount - totalDays;

      // 반차 선택 시 1일 이상 선택 불가능
      if (leaveType !== "종일") {
        if (totalDays > 0.5) {
          setOpenHalfDayLeaveErrorModal(true);
          setValue(FIELD.START_DATE.key, "");
          setValue(FIELD.END_DATE.key, "");
          setValue(FIELD.REMAINING_PAID_LEAVE.key, dayOffCount);
          setValue(FIELD.TOTAL_DAYS.key, 0);
          setResetDateRange(!resetDateRange);
          return;
        }
      }

      if (newRemainingLeave < 0) {
        // 남은 휴가 기간이 0보다 작으면 휴가 기간, 총 휴가 일수, 잔여 휴가 일수 초기화
        setOpenInsufficientRemainingLeaveModal(true);
        setValue(FIELD.START_DATE.key, "");
        setValue(FIELD.END_DATE.key, "");
        setValue(FIELD.REMAINING_PAID_LEAVE.key, dayOffCount);
        setValue(FIELD.TOTAL_DAYS.key, 0);
        setResetDateRange(!resetDateRange);
      } else {
        // 남은 휴가 기간이 0보다 크거나 같으면 총 휴가 일수, 잔여 휴가 일수 갱신
        setValue(FIELD.TOTAL_DAYS.key, totalDays);
        setValue(FIELD.REMAINING_PAID_LEAVE.key, newRemainingLeave);
      }
    }
  };

  return (
    <>
      <CreateDocumentClassFormBody noBorderTop>
        <CreateDocumentForm.Field label={FIELD.LEAVE_TYPE.label}>
          <CheckboxGroupStyle>
            <div>
              {FIELD.LEAVE_TYPE.options.map((option) => {
                return (
                  <Controller
                    key={option}
                    name={FIELD.LEAVE_TYPE.key}
                    control={control}
                    rules={{
                      required: "종류 필수",
                    }}
                    render={() => (
                      <CheckBox
                        value={option}
                        checked={watch(FIELD.LEAVE_TYPE.key) === option}
                        onCheckedChange={() => {
                          setValue(FIELD.LEAVE_TYPE.key, option, {
                            shouldDirty: true,
                          });
                          calculateTotalDays();
                        }}
                      >
                        {option}
                      </CheckBox>
                    )}
                  />
                );
              })}
            </div>

            {get(errors, FIELD.LEAVE_TYPE.key)?.message && (
              <label>{get(errors, FIELD.LEAVE_TYPE.key)?.message}</label>
            )}
          </CheckboxGroupStyle>
        </CreateDocumentForm.Field>

        <CreateDocumentForm.Field label={FIELD.LEAVE_REASON.label}>
          <DocumentTextField
            placeholder="사유를 입력해 주세요."
            {...register(FIELD.LEAVE_REASON.key, {
              minLength: {
                value: FIELD.LEAVE_REASON.minLength,
                message: formatMinMaxLengthMessage(
                  FIELD.LEAVE_REASON.minLength,
                  FIELD.LEAVE_REASON.maxLength,
                ),
              },
              maxLength: {
                value: FIELD.LEAVE_REASON.maxLength,
                message: formatMinMaxLengthMessage(
                  FIELD.LEAVE_REASON.minLength,
                  FIELD.LEAVE_REASON.maxLength,
                ),
              },
            })}
            errorMessage={errors[FIELD.LEAVE_REASON.key]?.message}
          />
        </CreateDocumentForm.Field>

        <CreateDocumentForm.Field label="기간">
          <InlineFieldWrapStyle>
            <DateRangePicker
              usage="document"
              placeholder={{ start: "시작일", end: "종료일" }}
              defaultValue={{
                start: tempDocumentDetail?.documentData?.[FIELD.START_DATE.key],
                end: tempDocumentDetail?.documentData?.[FIELD.END_DATE.key],
              }}
              value={{
                start: watch(FIELD.START_DATE.key),
                end: watch(FIELD.END_DATE.key),
              }}
              dateFormat={FIELD.START_DATE.dateFormat}
              onChangeDate={(_, dateString) => {
                setValue(FIELD.START_DATE.key, dateString?.start, {
                  shouldDirty: true,
                });
                setValue(FIELD.END_DATE.key, dateString?.end, {
                  shouldDirty: true,
                });
                calculateTotalDays();
              }}
              errorMessage={{
                start: errors[FIELD.START_DATE.key]?.message,
                end: errors[FIELD.END_DATE.key]?.message,
              }}
              onClickReset={resetDateRange}
            />
            <PeriodDescriptionStyle>
              (총{" "}
              <InlineNumberFieldStyle
                placeholder="0"
                readOnly
                {...register(FIELD.TOTAL_DAYS.key)}
              />{" "}
              일간)
            </PeriodDescriptionStyle>
          </InlineFieldWrapStyle>
        </CreateDocumentForm.Field>

        <CreateDocumentForm.Field label="사용 후 잔여휴가">
          <InlineFieldWrapStyle>
            <RemainingVacationWrapStyle>
              ({" "}
              <InlineNumberFieldStyle
                placeholder="0"
                readOnly
                {...register(FIELD.REMAINING_PAID_LEAVE.key)}
              />
              /
              <InlineNumberFieldStyle
                placeholder="0"
                readOnly
                {...register(FIELD.REMAINING_ANNUAL_LEAVE.key)}
              />{" "}
              일)
            </RemainingVacationWrapStyle>
            <InlineDescriptionStyle>(잔여휴가/연간휴가)</InlineDescriptionStyle>
          </InlineFieldWrapStyle>
        </CreateDocumentForm.Field>

        <CreateDocumentForm.Field label={FIELD.NOTES.label}>
          <DocumentTextField
            placeholder="추가 사항을 입력해 주세요."
            {...register(FIELD.NOTES.key, {
              minLength: {
                value: FIELD.NOTES.minLength,
                message: formatMinMaxLengthMessage(
                  FIELD.NOTES.minLength,
                  FIELD.NOTES.maxLength,
                ),
              },
              maxLength: {
                value: FIELD.NOTES.maxLength,
                message: formatMinMaxLengthMessage(
                  FIELD.NOTES.minLength,
                  FIELD.NOTES.maxLength,
                ),
              },
            })}
            errorMessage={errors[FIELD.NOTES.key]?.message}
          />
        </CreateDocumentForm.Field>
      </CreateDocumentClassFormBody>

      <Modal.Root
        open={openInsufficientRemainingLeaveModal}
        onOpenChange={setOpenInsufficientRemainingLeaveModal}
      >
        <Modal.Body title="잔여 휴가 부족" confirm>
          선택된 휴가 기간이 잔여 휴가 일수를 초과합니다.
        </Modal.Body>
      </Modal.Root>

      <Modal.Root
        open={openHalfDayLeaveErrorModal}
        onOpenChange={setOpenHalfDayLeaveErrorModal}
      >
        <Modal.Body
          title="오전/오후 반차는 1일 이상 선택할 수 없습니다. 다시 선택해주세요."
          confirm
        ></Modal.Body>
      </Modal.Root>
    </>
  );
};

export default LeaveApplicationForm;

/* 필드 스타일 */
const CheckboxGroupStyle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;

  & > div {
    display: flex;
    gap: 2.4rem;
  }

  & > label {
    padding-left: 1.6rem;
    ${({ theme }) => theme.typography.body3.regular};
    color: ${({ theme }) => theme.color.text.danger};
  }
`;

const InlineFieldWrapStyle = styled.div`
  display: flex;
  gap: 1rem;

  ${({ theme }) => theme.media.small} {
    flex-wrap: wrap;
  }
`;

const InlineNumberFieldStyle = styled(DocumentTextField)`
  width: 7.2rem;

  input {
    width: 100%;
    text-align: center;
  }
`;

const InlineDescriptionStyle = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.color.text.secondary};

  ${({ theme }) => theme.typography.body1.regular}
  letter-spacing: -0.048rem;
`;

const PeriodDescriptionStyle = styled.div`
  height: 5.6rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  white-space: nowrap;
`;

const RemainingVacationWrapStyle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
`;
